import { type AudioWithChapters, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAudio } from "react-use";

import Dialog from "../Dialog";
import AudioPlayerCard from "./AudioPlayerCard";
import { useAccess, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

const R2_STALL_TIMEOUT_MS = 6_000;

type Props = AudioWithChapters & {
	isPaid?: boolean;
};

const AudioPlayer = (props: Props) => {
	const { audio, chapters } = props;

	const { isMember } = useAccess();
	const { userId } = useAuth();

	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	const [isModalOpen, setIsModalOpen] = useState(false);

	// Presigned R2 URLs are re-signed hourly; swapping src mid-listen would
	// restart playback, so follow URL updates only until playback starts.
	const [src, setSrc] = useState(audio.url);
	const srcAudioIdRef = useRef(audio._id);
	const hasStartedRef = useRef(false);
	const usedFallbackRef = useRef(false);
	const resumeRef = useRef<{ time: number; play: boolean } | null>(null);
	const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (audio._id !== srcAudioIdRef.current) {
			srcAudioIdRef.current = audio._id;
			hasStartedRef.current = false;
			usedFallbackRef.current = false;
			if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
			stallTimerRef.current = null;
			setSrc(audio.url);
			return;
		}

		if (!hasStartedRef.current && !usedFallbackRef.current) {
			setSrc(audio.url);
		}
	}, [audio._id, audio.url]);

	const clearStallTimer = () => {
		if (stallTimerRef.current) {
			clearTimeout(stallTimerRef.current);
			stallTimerRef.current = null;
		}
	};

	useEffect(
		() => () => {
			if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
		},
		[],
	);

	const canUseFallback = () =>
		!!audio.fallbackUrl &&
		!usedFallbackRef.current &&
		src !== audio.fallbackUrl;

	// Retry once from Sanity at the same position.
	const switchToFallback = (play: boolean) => {
		if (!canUseFallback() || !audio.fallbackUrl) return;

		clearStallTimer();
		usedFallbackRef.current = true;
		resumeRef.current = {
			time: audioRef.current?.currentTime ?? 0,
			play,
		};
		setSrc(audio.fallbackUrl);
	};

	const [audioElement, state, controls, audioRef] = useAudio({
		src,
		id: "course-audio-player-tag",

		// R2 failed (outage, expired signature, bad object).
		onError: () => {
			switchToFallback(hasStartedRef.current && !state.paused);
		},

		// Chrome retries a failing range request for ~20s before firing
		// `error`; don't leave the listener in silence that long.
		onWaiting: () => {
			if (!canUseFallback() || stallTimerRef.current) return;

			const stalledAt = audioRef.current?.currentTime ?? 0;

			stallTimerRef.current = setTimeout(() => {
				stallTimerRef.current = null;
				const element = audioRef.current;

				if (
					element &&
					!element.paused &&
					element.currentTime - stalledAt < 0.5
				) {
					switchToFallback(true);
				}
			}, R2_STALL_TIMEOUT_MS);
		},

		onPlaying: clearStallTimer,

		onLoadedMetadata: () => {
			const element = audioRef.current;
			const resume = resumeRef.current;

			if (!element || !resume) return;

			resumeRef.current = null;
			element.currentTime = resume.time;

			if (resume.play) {
				element.play().catch(() => {});
			}
		},

		onPlay: () => {
			hasStartedRef.current = true;

			if (props.isPaid && !isMember) {
				trackCTA(
					userId ? UserAction.BECOME_A_MEMBER : UserAction.SIGN_UP,
					"Audio Player Play Button",
				);
				setUserAction(userId ? UserAction.BECOME_A_MEMBER : UserAction.SIGN_UP);

				audioRef.current?.pause();
			}
		},
	});

	const audioPlayer = audioRef.current!;

	const currentChapterIndex = useMemo(() => {
		const index = chapters.findLastIndex(
			(chapter) => state.time >= chapter.audioTimestamp,
		);

		return index === -1 ? 0 : index;
	}, [chapters, state.time]);

	const duration = audioRef.current?.duration;

	return (
		<>
			{audioElement}

			<button
				id="audio-player-button"
				className="hidden"
				onClick={() => setIsModalOpen(true)}
			/>

			<Dialog
				className="max-w-md"
				titleClassName="!text-lg"
				isOpen={isModalOpen}
				title={chapters[currentChapterIndex].title}
				onClose={() => setIsModalOpen(false)}
			>
				<div className="-mt-1 -mb-2">
					<AudioPlayerCard
						isModal
						{...props}
						state={state}
						duration={duration}
						currentChapterIndex={currentChapterIndex}
						audioPlayer={audioPlayer}
						controls={controls}
					/>
				</div>
			</Dialog>

			<AudioPlayerCard
				{...props}
				state={state}
				duration={duration}
				currentChapterIndex={currentChapterIndex}
				audioPlayer={audioPlayer}
				controls={controls}
			/>
		</>
	);
};
export default AudioPlayer;
